<template>
  <FlashNotifications ref="flash" />

  <!-- Toolbar: search + invite, no card wrapper -->
  <div class="mb-4 flex flex-wrap sm:flex-nowrap items-center gap-3">
    <div class="relative flex-1 min-w-[160px] max-w-xs">
      <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
      </svg>
      <input v-model="searchInput" @input="onSearchInput" type="search" :placeholder="I18N.features.bookings.search_by"
        class="w-full pl-8 pr-14 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-cancel-button]:appearance-none"/>
      <div class="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
        <svg v-if="isSearching" class="w-3.5 h-3.5 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
        </svg>
        <button v-if="searchInput" @click="clearSearch" type="button" aria-label="Clear search"
          class="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </div>

    <span class="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">{{ store.members_page.total_records || 0 }} members</span>

    <button v-if="canManageMembers" type="button" @click="store.showInviteModal = true"
      class="flex-shrink-0 ml-auto inline-flex items-center gap-2 px-4 py-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
      </svg>
      {{ I18N.features.organization.invite_team_members }}
    </button>
  </div>

  <!-- Loading skeleton -->
  <div v-if="store.members_loading && !store.org_members_fetched" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden animate-pulse">
    <div class="grid grid-cols-[1fr_auto_auto] items-center gap-4 px-6 py-3 bg-gray-50 dark:bg-gray-800/40">
      <span class="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded"></span>
      <span class="w-24 h-3 bg-gray-200 dark:bg-gray-700 rounded justify-self-center"></span>
      <span class="w-8"></span>
    </div>
    <div v-for="n in 4" :key="n" class="grid grid-cols-[1fr_auto_auto] items-center gap-4 px-6 py-4 border-t border-gray-50 dark:border-gray-800/60">
      <div class="flex items-center gap-3 min-w-0">
        <div class="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
        <div class="min-w-0 space-y-1.5 flex-1">
          <div class="h-3.5 w-1/3 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="h-3 w-1/2 bg-gray-100 dark:bg-gray-800 rounded"></div>
        </div>
      </div>
      <div class="w-16 h-5 bg-gray-100 dark:bg-gray-800 rounded justify-self-center"></div>
      <div class="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
    </div>
  </div>

  <!-- Error state -->
  <div v-else-if="store.members_error" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center py-16 text-center px-4">
    <div class="w-14 h-14 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
      <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
      </svg>
    </div>
    <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
    <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.members_error }}</p>
    <button @click="retryFetch" type="button" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
      Try again
    </button>
  </div>

  <!-- Table: column headers and pagination stay fixed in place; only the
       member rows in between scroll, once there are enough to need it. -->
  <div v-else class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden flex flex-col h-[calc(100vh-10rem)]">
    <div class="flex-shrink-0 grid grid-cols-[1fr_auto_auto] items-center gap-4 px-6 py-3 bg-gray-50 dark:bg-gray-800/40 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
      <button type="button" @click="setSort('user_email')"
        class="flex items-center gap-1 text-left hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
        {{ I18N.features.organization.member }}
        <svg v-if="store.members_sort_by === 'user_email'" class="w-3 h-3 flex-shrink-0 transition-transform" :class="store.members_sort_order === 'desc' ? 'rotate-180' : ''" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06.02L10 9.06l-3.71 3.75a.75.75 0 11-1.08-1.04l4.25-4.3a.75.75 0 011.08 0l4.25 4.3a.75.75 0 01-.02 1.06Z" clip-rule="evenodd"/>
        </svg>
      </button>
      <button type="button" @click="setSort('role')"
        class="w-24 flex items-center justify-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
        {{ I18N.features.organization.role }}
        <svg v-if="store.members_sort_by === 'role'" class="w-3 h-3 flex-shrink-0 transition-transform" :class="store.members_sort_order === 'desc' ? 'rotate-180' : ''" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06.02L10 9.06l-3.71 3.75a.75.75 0 11-1.08-1.04l4.25-4.3a.75.75 0 011.08 0l4.25 4.3a.75.75 0 01-.02 1.06Z" clip-rule="evenodd"/>
        </svg>
      </button>
      <span class="w-8"></span>
    </div>

    <!-- Scrollable rows -->
    <div class="flex-1 overflow-y-auto">
      <div v-if="store.org_members.length === 0" class="px-6 py-12 text-center">
        <p class="text-sm text-gray-400 dark:text-gray-500">
          {{ store.members_search_term ? `No members match "${store.members_search_term}".` : 'No members yet. Invite your team to get started.' }}
        </p>
      </div>

      <MemberRow v-for="member in store.org_members" :key="member.id" :member="member"
        @role-change="(role) => updateMemberRole(member.id, role)"
        @edit="openEditModal(member)"
        @resend="reInvite(member.id)"
        @remove="openRemoveModal(member.id)"
      />
    </div>

    <!-- Pagination -->
    <div v-if="store.members_page.total_pages > 1"
      class="flex-shrink-0 flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/20">
      <p class="text-xs text-gray-500 dark:text-gray-400 tabular-nums order-2 sm:order-1">
        Showing <span class="font-medium text-gray-700 dark:text-gray-300">{{ pageRangeStart }}–{{ pageRangeEnd }}</span>
        of <span class="font-medium text-gray-700 dark:text-gray-300">{{ store.members_page.total_records }}</span> members
      </p>

      <div class="flex items-center gap-1 order-1 sm:order-2">
        <button @click="prevPage" :disabled="store.members_page.current_page === 1" :aria-label="I18N.application.attendees.prev"
          class="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>

        <template v-for="(item, index) in pageItems" :key="index">
          <span v-if="item === '…'" class="w-8 h-8 flex items-center justify-center text-xs text-gray-400 dark:text-gray-500">…</span>
          <button v-else @click="goToPage(item)"
            class="w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold tabular-nums transition-colors"
            :class="item === store.members_page.current_page
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-300 border border-transparent hover:bg-white dark:hover:bg-gray-800 hover:border-gray-200 dark:hover:border-gray-700'">
            {{ item }}
          </button>
        </template>

        <button @click="nextPage" :disabled="store.members_page.current_page === store.members_page.total_pages" :aria-label="I18N.application.attendees.next"
          class="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    </div>
  </div>

  <!-- Invite modal -->
  <InviteMembers v-if="store.showInviteModal" />

  <!-- Remove member modal -->
  <Teleport to="body">
    <div v-if="showRemoveModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="showRemoveModal = false"></div>
      <div class="relative bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 w-full max-w-md p-6">
        <h3 class="text-base font-bold text-gray-900 dark:text-white">{{ I18N.features.organization.remove_member }}?</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-2">{{ I18N.features.organization.remove_modal_subheading }}</p>
        <div class="flex gap-3 pt-5 mt-5 border-t border-gray-100 dark:border-gray-700">
          <button @click="showRemoveModal = false" type="button"
            class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
            {{ I18N.application.bookings.upcoming.cancel }}
          </button>
          <button @click="deleteMember" type="button"
            class="flex-1 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center justify-center gap-2">
            <svg v-if="removing" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            {{ I18N.features.organization.remove }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Edit member modal -->
  <Teleport to="body">
    <div v-if="showEditModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="showEditModal = false"></div>
      <div class="relative bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 w-full max-w-md p-6">
        <h3 class="text-base font-bold text-gray-900 dark:text-white mb-5">{{ I18N.features.organization.update_member }}</h3>

        <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">{{ I18N.features.organization.designation }}</label>
        <input v-model="tempMember.designation" type="text" :placeholder="I18N.features.organization.write_designation"
          class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all mb-4"/>

        <template v-if="!tempMember.org_owner">
          <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">{{ I18N.features.organization.role }}</label>
          <div class="grid grid-cols-2 gap-2">
            <button type="button" @click="tempMember.role = 'admin'"
              class="rounded-lg text-left py-2.5 px-3 border transition-colors"
              :class="tempMember.role === 'admin' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'">
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ I18N.features.organization.admin }}</p>
            </button>
            <button type="button" @click="tempMember.role = 'member'"
              class="rounded-lg text-left py-2.5 px-3 border transition-colors"
              :class="tempMember.role === 'member' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'">
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ I18N.features.organization.member }}</p>
            </button>
          </div>
        </template>
        <p v-else class="text-xs text-gray-400 dark:text-gray-500">
          <span class="font-semibold text-amber-600 dark:text-amber-400">Owner</span> — the organization owner's role can't be changed.
        </p>

        <div class="flex gap-3 pt-5 mt-5 border-t border-gray-100 dark:border-gray-700">
          <button @click="showEditModal = false" type="button"
            class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
            {{ I18N.application.bookings.upcoming.cancel }}
          </button>
          <button @click="updateMember" type="button"
            class="flex-1 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
            {{ I18N.website_content.account_settings.personal.save }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed } from 'vue'
import { orgStore } from 'controllers/vue_components/organizations/store'
import { useRequest } from 'composables/useRequest.js'
import { useConstants } from 'composables/useConstants.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import MemberRow from 'controllers/vue_components/organizations/settings/MemberRow'
import InviteMembers from 'controllers/vue_components/organizations/settings/invite_member_modal'

const flash = ref(null)
const { axiosRequest } = useRequest(flash)
const { I18N } = useConstants()
const store = orgStore()

const searchInput = ref(store.members_search_term)
const isSearching = ref(false)
let searchDebounceTimer = null

const showRemoveModal = ref(false)
const showEditModal = ref(false)
const removing = ref(false)
const deletableMemberId = ref(null)
const tempMember = ref({})

// Only admins/the owner may invite new members — the backend already enforces
// this (Api::V1::Organizations::MembersController#create), this just keeps
// the button from being shown to people who can't use it.
const canManageMembers = computed(() => {
  const membership = store.user_role_in_org?.users_organizations
  return membership?.role === 'admin' || membership?.org_owner === true
})

const pageRangeStart = computed(() => {
  if (store.members_page.total_records === 0) return 0
  return (store.members_page.current_page - 1) * store.members_page.limit + 1
})

const pageRangeEnd = computed(() => Math.min(store.members_page.current_page * store.members_page.limit, store.members_page.total_records))

// Windows the page numbers around the current page (with an ellipsis for the
// gap) instead of rendering a button per page, which would be unusable once
// an organization has more than a handful of pages of members.
const pageItems = computed(() => {
  const total = store.members_page.total_pages
  const current = store.members_page.current_page
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)

  const items = [1]
  if (current > 3) items.push('…')
  for (let page = Math.max(2, current - 1); page <= Math.min(total - 1, current + 1); page++) items.push(page)
  if (current < total - 2) items.push('…')
  items.push(total)
  return items
})

async function goToPage(page) {
  if (page === store.members_page.current_page) return
  store.members_page.number = page
  await store.fetchOrgMembers()
}

async function setSort(column) {
  if (store.members_sort_by === column) {
    store.members_sort_order = store.members_sort_order === 'asc' ? 'desc' : 'asc'
  } else {
    store.members_sort_by = column
    store.members_sort_order = 'asc'
  }
  store.members_page.number = 1
  await store.fetchOrgMembers()
}

function onSearchInput() {
  clearTimeout(searchDebounceTimer)
  searchDebounceTimer = setTimeout(async () => {
    store.members_search_term = searchInput.value
    store.members_page.number = 1
    isSearching.value = true
    await store.fetchOrgMembers()
    isSearching.value = false
  }, 300)
}

async function clearSearch() {
  searchInput.value = ''
  clearTimeout(searchDebounceTimer)
  store.members_search_term = ''
  store.members_page.number = 1
  isSearching.value = true
  await store.fetchOrgMembers()
  isSearching.value = false
}

function retryFetch() {
  store.fetchOrgMembers()
}

function openRemoveModal(id) {
  deletableMemberId.value = id
  showRemoveModal.value = true
}

function openEditModal(member) {
  tempMember.value = { ...member }
  showEditModal.value = true
}

async function updateMember() {
  const payload = { designation: tempMember.value.designation }
  if (!tempMember.value.org_owner) payload.role = tempMember.value.role

  const res = await axiosRequest('PUT', `/api/v1/organizations/${store.current_org.id}/members/${tempMember.value.id}`,
    { member: payload }, false)
  if (res.js_error === false && res.response.success) {
    flash.value?.show(I18N.features.organization.member_updated, 'notice')
    const member = store.org_members.find((m) => m.id === tempMember.value.id)
    if (member) Object.assign(member, res.response.data)
    showEditModal.value = false
  } else {
    flash.value?.show(res.response?.message || 'Something went wrong', 'alert')
  }
}

async function deleteMember() {
  removing.value = true
  const res = await axiosRequest('DELETE', `/api/v1/organizations/${store.current_org.id}/members/${deletableMemberId.value}`, {}, false)
  if (res.js_error === false && res.response.success) {
    await store.fetchOrgMembers()
    flash.value?.show(I18N.features.organization.member_removed, 'notice')
    showRemoveModal.value = false
  } else {
    flash.value?.show(res.response?.message || 'Something went wrong', 'alert')
  }
  removing.value = false
}

async function updateMemberRole(id, role) {
  const res = await axiosRequest('PUT', `/api/v1/organizations/${store.current_org.id}/members/${id}`, { member: { role } }, false)
  if (res.js_error === false && res.response.success) {
    flash.value?.show(I18N.features.organization.role_updated, 'notice')
    const member = store.org_members.find((m) => m.id === id)
    if (member) Object.assign(member, res.response.data)
  } else {
    flash.value?.show(res.response?.message || 'Something went wrong', 'alert')
  }
}

async function reInvite(id) {
  const res = await axiosRequest('POST', `/api/v1/organizations/${store.current_org.id}/members/${id}/re_invite`, {}, false)
  if (res.js_error === false && res.response.success) {
    flash.value?.show(res.response.message, 'notice')
    const member = store.org_members.find((m) => m.id === id)
    if (member) Object.assign(member, res.response.data)
  } else {
    flash.value?.show(res.response?.message || 'Something went wrong', 'alert')
  }
}

async function nextPage() {
  if (store.members_page.current_page < store.members_page.total_pages) {
    store.members_page.number++
    await store.fetchOrgMembers()
  }
}

async function prevPage() {
  if (store.members_page.current_page > 1) {
    store.members_page.number--
    await store.fetchOrgMembers()
  }
}
</script>
